import React, { useEffect, useMemo, useState } from 'react';
import { Title } from '../../components/ui/Title';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/Button';
import { Pagination } from '../../components/ui/Pagination';
import styled from 'styled-components';
import { RegisterList } from '../../components/layout/RegisterList';
import { RegisterTable } from '../../components/ui/RegisterTable';
import {
  useScrapsQuery,
  useDeleteScrapMutation,
  useLinesFromFactoriesQuery,
  useMachinesFromLineQuery,
  WorkShift,
  useScrapItemTypeQuery,
  useScrapDefectNameQuery,
  useAllGroupsQuery,
} from '../../hooks/graphqlQueries';
import { useFeedback } from '../../context/feedback';
import { t } from 'i18next';
import { format, parseISO } from 'date-fns';
import { PageOptions } from '../../components/layout/PageOptions';
import { Footer } from '../../components/layout/Footer';
import { Drop } from '../../components/ui/Drop';
import { MultiDropdown } from '../../components/ui/MultiDropdown';
import { Calendar } from '../../components/ui/Calendar';
import { toEndShiftC, toStartShiftA } from '../../scripts/times';
import { downloadFile, makeCSVFromTable } from '../../scripts/exportFiles';
import { asString } from 'ts-runtime-typecheck';
import { getPagination } from '../../scripts/pagination';
import { ClearFilter } from '../../components/layout/History/History';
import { Icon } from '../../components/ui/Icon';

type attributeType = {
  name: string;
  attr: string;
};

const attributes: attributeType[] = [
  { name: 'Data', attr: 'date' },
  { name: 'Valor', attr: 'amount' },
  { name: 'Máquina', attr: 'machine' },
  { name: 'Turno', attr: 'workshift' },
  { name: 'Item', attr: 'item' },
  { name: 'Grupo Detector', attr: 'detectorGroup' },
  { name: 'Grupo Gerador', attr: 'generatorGroup' },
  { name: 'Defeito', attr: 'defect' },
];

const shiftTimes = [
  { id: WorkShift.ShiftA, name: 'Turno A' },
  { id: WorkShift.ShiftB, name: 'Turno B' },
  { id: WorkShift.ShiftC, name: 'Turno C' },
];

const Filters = styled.div`
  display: grid;
  grid-area: Filters;
  grid-template-columns: repeat(auto-fit, minmax(179px, max-content));
  column-gap: 20px;
  row-gap: 10px;
  width: 1fr;
  justify-content: right;

  @media (max-width: 600px) {
    justify-content: center;
  }
`;

const Table = styled.div`
  display: grid;
  grid-area: Table;

  font-family: ${(props) => props.theme.font};
  font-style: normal;
  font-weight: 500;
  color: ${(props) => props.theme.colors.table.colorBody};

  @media (max-width: 1168px) {
    &&:after {
      font-size: 12px;
      padding-top: 5px;
      content: 'Deslize a tabela para os lados';
    }
  }
`;

type PageParameters = {
  page: number;
  limit: number;
  filters: {
    factory?: { id: string; name: string };
    line?: { id: string; name: string };
    machine?: { id: string; name: string };
    workShift: WorkShift[];
    startDate: Date;
    endDate: Date;
    item?: string;
    detectorGroup?: string;
    generatorGroup?: string;
    defect?: string;
  };
};


const initialParameters: PageParameters = {
  page: 1,
  limit: 10,
  filters: {
    workShift: [],
    startDate: toStartShiftA(new Date()),
    endDate: toEndShiftC(new Date()),
  },
};

export const ScrapListPage: React.FC = () => {
  const navigate = useNavigate();
  const feedback = useFeedback();

  const rescuedFilters = sessionStorage.getItem('GlobalHistoryFilter');
  const storagedFilters: PageParameters = rescuedFilters ? JSON.parse(rescuedFilters) : null;
  const [parameters, setParameters] = useState<PageParameters>(
    storagedFilters
      ? {
          ...storagedFilters,
          limit: 10,
          page: 1,
          filters: {
            ...storagedFilters.filters,
            factory: undefined,
            machine: undefined,
            line: undefined,
            startDate: new Date(storagedFilters.filters.startDate),
            endDate: new Date(storagedFilters.filters.endDate),
          },
        }
      : initialParameters
  );

  const [groupName] = useAllGroupsQuery();

  const [lineName] = useLinesFromFactoriesQuery();
  const [machineName] = useMachinesFromLineQuery({
    pause: parameters.filters.line?.id === undefined,
    requestPolicy: 'network-only',
    variables: {
      ...parameters.filters,
      lineId: parameters.filters.line?.id ?? '',
    },
  });
  const [itemName] = useScrapItemTypeQuery({ requestPolicy: 'network-only' });
  const [defectName] = useScrapDefectNameQuery({ requestPolicy: 'network-only' });
  const [isExportData, setIsExportData] = useState(false);
  const dataExportLimit = parseInt(asString(process.env['DATA_EXPORT_LIMIT']));
  const [scrapsQuery, refetch] = useScrapsQuery({
    requestPolicy: 'network-only',
    variables: {
      ...getPagination(parameters.page, parameters.limit, { dataExportLimit, isExportData }),
      lineId: parameters.filters.line?.id ?? '',
      machineId: parameters.filters.machine?.id ?? '',
      workShift: parameters.filters.workShift ?? [],
      startDate: parameters.filters.startDate,
      endDate: parameters.filters.endDate,
      detectorGroup: parameters.filters.detectorGroup ?? '',
      generatorGroup: parameters.filters.generatorGroup ?? '',
      item: parameters.filters.item ?? '',
    },
  });

  const [, deleteScrap] = useDeleteScrapMutation();
  const scraps = useMemo(() => scrapsQuery.data?.scraps?.value ?? [], [scrapsQuery.data]);

  useEffect(() => {
    refetch();
  }, [parameters]);

  useEffect(() => {
    sessionStorage.setItem('GlobalHistoryFilter', JSON.stringify(parameters));
  }, [parameters]);

  const data = useMemo(
    () =>
      scraps.map((scrap) => {
        return {
          table: {
            date: format(parseISO(scrap.date), 'dd/MM/yyyy'),
            amount: `${scrap.amount}`,
            machine: `${scrap.machine?.code}`,
            workshift: t(scrap.workShift),
            item: scrap.details?.item ?? '',
            detectorGroup: scrap.details?.detectorGroup ?? '',
            generatorGroup: scrap.details?.generatorGroup ?? '',
            defect: scrap.details?.scrapPossibleFailure ?? '',
          },
          value: { ...scrap },
        };
      }),
    [scraps]
  );

  const [tableData, setTableData] = useState<typeof data>(data);
  const thisDate = format(parseISO(new Date().toISOString()), 'dd-MM-yyyy-HH-mm-ss');

  useEffect(() => {
    if (isExportData) {
      downloadFile(
        `scraps-${thisDate}`,
        makeCSVFromTable(
          attributes,
          data.map((v) => v.table)
        )
      );
      setIsExportData(false);

      if (scraps.length === dataExportLimit)
        feedback.message({
          animation: 'Error',
          title: 'Limite de dados atingido!',
          subtitle: `Não é possível exportar mais que ${dataExportLimit} linhas de dados.`,
        });
    } else setTableData(data);
  }, [data]);

  return (
    <RegisterList>
      <Title>Scraps cadastrados</Title>
      <Filters>
        <Drop
          name="Grupo Detector"
          options={groupName.data?.groups?.value?.map((group) => ({ id: group.id, name: group.name })) ?? []}
          value={parameters.filters.detectorGroup}
          onSelect={(group) => {
            setParameters({
              ...parameters,
              filters: {
                ...parameters.filters,
                detectorGroup: group.name,
              },
            });
          }}
          errorMessage={groupName.data?.groups?.value?.length === 0 ? 'Não existe nenhum grupo cadastrado.' : undefined}
        />
        <Drop
          name="Linha"
          value={parameters.filters.line?.name}
          options={lineName.data?.lines?.value?.map((line) => ({ id: line.id, name: line.code })) ?? []}
          onSelect={(line) => {
            setParameters({
              limit: parameters.limit,
              page: 1,
              filters: { ...parameters.filters, line, machine: undefined },
            });
          }}
          errorMessage={lineName.data?.lines?.value?.length === 0 ? 'Não existe nenhuma linha cadastrada.' : undefined}
        />
        <Drop
          name="Máquina"
          value={parameters.filters.machine?.name}
          options={machineName.data?.machines?.value?.map((machine) => ({ id: machine.id, name: machine.code })) ?? []}
          onSelect={(machine) =>
            setParameters({ limit: parameters.limit, page: 1, filters: { ...parameters.filters, machine } })
          }
          disabled={!parameters.filters.line?.id}
          errorMessage={
            machineName.data?.machines?.value?.length === 0 ? 'Não existe nenhuma máquina cadastrada.' : undefined
          }
        />
        <Drop
          name="Item"
          options={
            itemName.data?.itemTypes?.value?.map((item) => ({
              id: item.id,
              name: item.name,
            })) ?? []
          }
          onSelect={(item) => {
            setParameters({
              limit: parameters.limit,
              page: 1,
              filters: { ...parameters.filters, item: item.name },
            });
          }}
          value={parameters.filters.item}
          errorMessage={
            itemName.data?.itemTypes?.value?.length === 0 ? 'Não existe nenhum tipo de item cadastrado.' : undefined
          }
        />
        <MultiDropdown
          name="Turno"
          value={parameters.filters.workShift ?? []}
          options={shiftTimes}
          onBlur={(shifts) => {
            if (shifts.length === 0) return;
            setParameters({
              limit: parameters.limit,
              page: 1,
              filters: { ...parameters.filters, workShift: shifts.map((el) => el.id as WorkShift) },
            });
          }}
          errorMessage={shiftTimes.length === 0 ? 'Não existe nenhum turno cadastrado.' : undefined}
        />
        <Calendar
          placeholder="Data inicial"
          value={format(parameters.filters.startDate, 'yyyy-MM-dd')}
          onChange={(date) => {
            const startDate = parseISO(date.target.value);
            if (date.target.value.length == 10)
              setParameters({ limit: parameters.limit, page: 1, filters: { ...parameters.filters, startDate } });
          }}
          onBlur={(date) => {
            const startDate = parseISO(date.target.value);
            if (date.target.value.length == 10)
              setParameters({ limit: parameters.limit, page: 1, filters: { ...parameters.filters, startDate } });
          }}
        />
        <Calendar
          placeholder="Data final"
          value={format(parameters.filters.endDate, 'yyyy-MM-dd')}
          onChange={(date) => {
            const endDate = parseISO(date.target.value);
            if (date.target.value.length == 10)
              setParameters({ limit: parameters.limit, page: 1, filters: { ...parameters.filters, endDate } });
            }
          }
          onBlur={(date) => {
            const endDate = parseISO(date.target.value);
            if (date.target.value.length == 10)
              setParameters({ limit: parameters.limit, page: 1, filters: { ...parameters.filters, endDate } });
          }}
        />
        <Drop
          name="Defeito"
          options={
            machineName.data?.machines?.value
              ?.find((machine) => machine.id === parameters.filters.machine?.id)
              ?.possibleDefects?.map((defect) => ({ id: defect, name: defect })) ?? []
          }
          onSelect={(selected) => {
            setParameters({ ...parameters, filters: { ...parameters.filters, defect: selected.name } });
          }}
          value={parameters.filters.defect}
          disabled={!parameters.filters.machine}
          errorMessage={
            defectName.data?.scrapPossibleFailures?.value?.length === 0
              ? 'Não existe nenhum defeito cadastrado.'
              : undefined
          }
        />
        <Drop
          name="Grupo Gerador"
          options={groupName.data?.groups?.value?.map((group) => ({ id: group.id, name: group.name })) ?? []}
          onSelect={(group) => {
            setParameters({
              ...parameters,
              filters: {
                ...parameters.filters,
                generatorGroup: group.name,
              },
            });
          }}
          value={parameters.filters.generatorGroup}
          errorMessage={groupName.data?.groups?.value?.length === 0 ? 'Não existe nenhum grupo cadastrado.' : undefined}
        />
      </Filters>
      {parameters.filters.line && (
        <ClearFilter
          onClick={() => {
            setParameters({
              ...initialParameters,
              filters: {
                ...initialParameters.filters,
                startDate: parameters.filters.startDate,
                endDate: parameters.filters.endDate,
              },
            });
            sessionStorage.removeItem('GlobalHistoryFilter');
          }}
        >
          <Icon name="Cross" size="smaller" color="white" />
          <span>Limpar Filtros</span>
        </ClearFilter>
      )}
      <Table>
        <RegisterTable
          attributes={attributes}
          content={tableData}
          onDelete={(scrap) => {
            feedback.confirmation({
              title:
                'Deseja mesmo excluir esse scrap? Essa operação é irreversível e irá exluir também todos os dados associados ao scrap.',
              onHandle: (result) => {
                scrap;
                if (result) {
                  deleteScrap({ id: scrap['id']?.toString() ?? '' })
                    .then(() => {
                      feedback.message({
                        animation: 'Confetti',
                        title: 'Scrap deletada com sucesso',
                      });
                      parameters.page === 1 ? refetch() : setParameters({ ...parameters, page: 1 });
                    })
                    .catch(() => {
                      feedback.message({
                        animation: 'Error',
                        title: 'Erro ao deletar scrap',
                      });
                    });
                }
              },
            });
          }}
          onEdit={(scrap) => {
            navigate('edit', {
              state: {
                data: {
                  id: scrap.id,
                  amount: `${scrap.amount}`,
                  detailLine: scrap.details?.line,
                  detailItem: scrap.details?.item,
                  detailUser: scrap.details?.user,
                  detailScrapPossibleFailure: scrap.details?.scrapPossibleFailure,
                  detailDescription: scrap.details?.description,
                  detailDetectorGroup: scrap.details?.detectorGroup,
                  detailGeneratorGroup: scrap.details?.generatorGroup,
                  machineId: scrap.machine?.id,
                  machineCode: scrap.machine?.code,
                  lineId: scrap.machine?.line?.id,
                  lineCode: scrap.machine?.line?.code,
                  workShift: scrap.workShift,
                  date: scrap.date,
                },
                pagination: { ...parameters },
              },
            });
          }}
        />
      </Table>
      <Footer>
        <PageOptions>
          <Button color="light" title="Menu cadastros" icon="ArrowLeft" onClick={() => navigate('..')} />
          <Button title="Exportar" icon="Export" disabled={data.length == 0} onClick={() => setIsExportData(true)} />
        </PageOptions>
        <Pagination
          numberOfItems={parameters.limit}
          onSelectNumber={(number) => setParameters({ ...parameters, limit: number, page: 1 })}
          page={parameters.page}
          totalPages={Math.ceil((scrapsQuery.data?.scraps?.count ?? parameters.limit) / parameters.limit)}
          onPrevious={() => {
            setParameters({ ...parameters, page: parameters.page - 1 });
          }}
          onNext={() => {
            setParameters({ ...parameters, page: parameters.page + 1 });
          }}
        />
      </Footer>
    </RegisterList>
  );
};
